/**
 * كل الطلبات — عرض مسطّح مع مدى تاريخي وتصفّح صفحي
 * الأعمدة تُبنى من الأصناف المفعّلة، فتتوسّع تلقائياً
 */
import React, { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "../../api/client";
import DateRangeFilter, { defaultRange } from "../../components/shared/DateRangeFilter";
import type { DateRange } from "../../components/shared/DateRangeFilter";
import { useActiveItems } from "../../hooks/useItems";
import { formatDate, formatDateTime, formatNumber } from "../../lib/date";
import type { MealEntry } from "../../types";
import { useAuth } from "../../contexts/AuthContext";
import AdminEntryActions from "./AdminEntryActions";

interface Paged { data: MealEntry[]; meta: { total: number; page: number; pageSize: number; totalPages: number } }

export default function AllDataPage() {
  const [range, setRange] = useState<DateRange>(defaultRange());
  const [page,  setPage]  = useState(1);
  const { isRole } = useAuth();
  const isAdmin = isRole("SYSTEM_ADMIN");

  const { data: items = [] } = useActiveItems();

  const { data, isLoading, error } = useQuery<Paged>({
    queryKey: ["meal-entries", range.from, range.to, page],
    enabled:  range.from <= range.to,
    queryFn: () =>
      api.get<Paged>(
        `/meal-entries?from=${range.from}&to=${range.to}&page=${page}&pageSize=50`
      ).then((r) => r.data),
  });

  const rows = data?.data ?? [];

  /** خريطة الكميات لكل سجل — بحث O(1) بدل find داخل الحلقة */
  const qtyMaps = useMemo(
    () => new Map(rows.map((e) => [e.id, new Map(e.lines.map((l) => [l.itemId, l.qty]))])),
    [rows]
  );

  const exportCsv = () =>
    window.open(
      `${api.defaults.baseURL ?? ""}/meal-entries?from=${range.from}&to=${range.to}&format=csv`,
      "_blank"
    );

  return (
    <div>
      <div className="page-header flex items-start justify-between">
        <div>
          <h1 className="page-title">كل الطلبات</h1>
          <p className="page-subtitle">عرض تفصيلي لكل طلب يومي خلال المدى المحدد</p>
        </div>
        <button className="btn-secondary print:hidden" onClick={exportCsv} disabled={!rows.length}>
          تصدير CSV
        </button>
      </div>

      <div className="print:hidden">
        <DateRangeFilter value={range} onChange={(r) => { setRange(r); setPage(1); }} />
      </div>

      {isLoading && <div className="card text-center text-gray-400 py-10">جاري التحميل...</div>}
      {error     && <div className="alert-error">تعذّر تحميل البيانات</div>}

      {!isLoading && rows.length === 0 && (
        <div className="card text-center text-gray-400 py-10">لا توجد طلبات في هذا المدى</div>
      )}

      {rows.length > 0 && (
        <>
          <div className="card overflow-x-auto">
            <table className="w-full text-sm whitespace-nowrap">
              <thead>
                <tr className="border-b-2 border-gray-200 text-xs">
                  <th className="text-right py-2 px-2 sticky right-0 bg-white z-10">يوم التنفيذ</th>
                  <th className="text-right py-2 px-2">المحافظة</th>
                  <th className="text-right py-2 px-2">المديرية</th>
                  <th className="text-right py-2 px-2">المطعم</th>
                  <th className="text-right py-2 px-2">ملاحظات</th>
                  {items.map((it) => (
                    <th key={it.id} className="text-center py-2 px-2">{it.nameAr}</th>
                  ))}
                  <th className="text-right py-2 px-2">أُرسل في</th>
                  <th className="text-right py-2 px-2">الحالة</th>
                  <th className="text-right py-2 px-2">الاستلام</th>
                  {isAdmin && <th className="text-right py-2 px-2 print:hidden">إجراءات</th>}
                </tr>
              </thead>
              <tbody>
                {rows.map((e) => {
                  const q = qtyMaps.get(e.id)!;
                  return (
                    <tr key={e.id} className="border-b hover:bg-gray-50">
                      <td className="py-2 px-2 sticky right-0 bg-white" dir="ltr">{formatDate(e.entryDate)}</td>
                      <td className="py-2 px-2">{e.governorate.nameAr}</td>
                      <td className="py-2 px-2">{e.directorate.nameAr}</td>
                      <td className="py-2 px-2 text-gray-500">{e.restaurant?.name ?? "—"}</td>
                      <td className="py-2 px-2 text-gray-500 max-w-[10rem] truncate" title={e.notes ?? undefined}>
                        {e.notes || "—"}
                      </td>
                      {items.map((it) => (
                        <td key={it.id} className="text-center py-2 px-2" dir="ltr">
                          {q.has(it.id) ? formatNumber(q.get(it.id)!) : "—"}
                        </td>
                      ))}
                      <td className="py-2 px-2 text-gray-500 text-xs" dir="ltr">
                        {formatDateTime(e.submittedAt)}
                      </td>
                      <td className="py-2 px-2 text-xs">
                        {e.lockedAt
                          ? <span className="text-gray-500">مقفل</span>
                          : <span className="text-green-600">مفتوح</span>}
                        {e.isLateOverride && (
                          <span className="text-amber-600 mr-1" title="سُجّل بعد موعد الإقفال">
                            تجاوز
                          </span>
                        )}
                      </td>
                      <td className="py-2 px-2 text-xs">
                        {e.receivedAt
                          ? <span className="text-primary-700" title={e.receiptNotes ?? undefined}>
                              مستلَم{e.lines.some((l) => l.receivedQty != null && l.receivedQty !== l.qty) && (
                                <span className="text-red-700 mr-1">(بفرق)</span>
                              )}
                            </span>
                          : <span className="text-gray-400">لم يُستلم</span>}
                      </td>
                      {isAdmin && (
                        <td className="py-2 px-2 print:hidden"><AdminEntryActions entry={e} /></td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-between mt-3 text-sm print:hidden">
            <span className="text-gray-500">
              {data!.meta.total} طلب — صفحة {data!.meta.page} من {data!.meta.totalPages}
            </span>
            <div className="flex gap-2">
              <button className="btn-secondary" disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)}>السابق</button>
              <button className="btn-secondary" disabled={page >= data!.meta.totalPages}
                onClick={() => setPage((p) => p + 1)}>التالي</button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
